import { useNavigate } from "react-router-dom";

const HeroSection = ({ className = "" }) => {
    const navigate = useNavigate();

    return (
        <section className={`relative min-h-screen flex items-center justify-center overflow-hidden pt-24 pb-16 px-4 md:px-12 lg:px-20 ${className}`}>
            {/* Background gradient orbs */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-1/4 -left-32 w-96 h-96 bg-primary/20 rounded-full blur-3xl animate-pulse" />
                <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-secondary/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-accent/10 rounded-full blur-3xl" />
            </div>

            <div className="relative z-10 max-w-7xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                    {/* Left Content */}
                    <div className="text-center lg:text-left">
                        {/* Eyebrow */}
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-8">
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                            </span>
                            <span className="text-sm font-medium text-primary">AI-Powered Trading Platform</span>
                        </div>

                        {/* Main Headline */}
                        <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold font-heading leading-tight mb-6">
                            <span className="text-base-content">Unlock the </span>
                            <span className="text-gradient-primary">Power of AI</span>
                            <br />
                            <span className="text-base-content">in Stock Trading</span>
                        </h1>

                        {/* Subheadline */}
                        <p className="text-lg md:text-xl text-base-content/70 max-w-xl mb-10 leading-relaxed">
                            Analyse, research, build, backtest, and deploy intelligent trading strategies
                            for <span className="font-semibold text-primary">NSE & BSE</span> — no coding required
                        </p>

                        {/* CTAs */}
                        <div className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4">
                            <button
                                className="btn btn-primary btn-lg px-8 shadow-xl shadow-primary/30 hover:shadow-primary/50 hover:scale-105 transition-all duration-300"
                                onClick={() => navigate('/signup')}
                            >
                                Get Early Access
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                </svg>
                            </button>
                            <button
                                className="btn btn-ghost btn-lg gap-2 hover:bg-base-200"
                                onClick={() => {/* TODO: Open demo video */ }}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Watch Demo
                            </button>
                        </div>

                        {/* Trust indicators */}
                        <div className="mt-12 pt-8 border-t border-base-300/50">
                            <p className="text-sm text-base-content/50 mb-4">Trusted by traders across India</p>
                            <div className="flex items-center justify-center lg:justify-start gap-8 opacity-60">
                                <img src="/idx-1@2x.png" alt="IDX" className="h-8 object-contain grayscale hover:grayscale-0 transition-all" />
                                <img src="/ojk-1@2x.png" alt="OJK" className="h-8 object-contain grayscale hover:grayscale-0 transition-all" />
                            </div>
                        </div>
                    </div>

                    {/* Right Image */}
                    <div className="relative hidden lg:block">
                        <div className="relative">
                            {/* Glow effect behind image */}
                            <div className="absolute -inset-4 bg-gradient-to-r from-primary/30 via-secondary/30 to-accent/30 rounded-3xl blur-2xl opacity-60 animate-pulse"></div>

                            {/* Image container with glassmorphism */}
                            <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-base-100/5 backdrop-blur-sm shadow-2xl">
                                <img
                                    src="/trading-hero.png"
                                    alt="AI-Powered Stock Trading Visualization"
                                    className="w-full h-auto object-cover"
                                />

                                {/* Overlay gradient */}
                                <div className="absolute inset-0 bg-gradient-to-t from-base-100/20 to-transparent pointer-events-none"></div>
                            </div>

                            {/* Floating stats card */}
                            <div className="absolute -bottom-6 -left-6 bg-base-100 rounded-xl p-4 shadow-xl border border-base-300 animate-float">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-success/20 flex items-center justify-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="text-xs text-base-content/60">Strategy Returns</p>
                                        <p className="text-lg font-bold text-success">+24.8%</p>
                                    </div>
                                </div>
                            </div>

                            {/* Floating AI indicator */}
                            <div className="absolute -top-4 -right-4 bg-base-100 rounded-xl px-4 py-2 shadow-xl border border-base-300 animate-float" style={{ animationDelay: '0.5s' }}>
                                <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 rounded-full bg-primary animate-pulse"></div>
                                    <span className="text-sm font-medium">AI Analyzing</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Scroll indicator */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-base-content/40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
            </div>
        </section>
    );
};

export default HeroSection;
